(() => {
  'use strict';

  if (window.collectionCardAjaxCartInitialized) return;
  window.collectionCardAjaxCartInitialized = true;

  const SELECTORS = {
    form: '.js-collection-product-form',
    card: '[data-collection-product-card]',
    variant: '.collection-variant-select',
    variantData: '.collection-single-variant-data',
    quantity: '.collection-quantity-input',
    quantityButton: '.collection-quantity-arrow',
    submit: '.collection-add-to-cart',
    buttonText: '.collection-button-text',
    message: '.collection-form-message'
  };

  const getRootUrl = () => {
    if (window.Shopify?.routes?.root) return window.Shopify.routes.root;
    return '/';
  };

  const ensureJsonRoute = (url) => {
    if (!url) return '';
    return url.endsWith('.js') ? url : `${url}.js`;
  };

  const getCartAddUrl = () => {
    const themeRoute = ensureJsonRoute(window.routes?.cart_add_url);
    return themeRoute || `${getRootUrl()}cart/add.js`;
  };

  const getCartUrl = () => {
    const themeRoute = ensureJsonRoute(window.routes?.cart_url);
    return themeRoute || `${getRootUrl()}cart.js`;
  };

  const setMessage = (form, text = '', type = '') => {
    const message = form.querySelector(SELECTORS.message);
    if (!message) return;

    message.textContent = text;
    message.hidden = !text;
    message.classList.toggle('is-error', type === 'error');
    message.classList.toggle('is-success', type === 'success');
  };

  const getSelectedPriceSource = (form) => {
    const select = form.querySelector(SELECTORS.variant);

    if (select?.selectedOptions?.[0]) {
      return select.selectedOptions[0];
    }

    return form.querySelector(SELECTORS.variantData);
  };

  const isVariantAvailable = (priceSource) => {
    if (!priceSource) return true;
    if (priceSource.dataset.available === 'true') return true;
    if (priceSource.dataset.available === 'false') return false;
    return !priceSource.disabled;
  };

  const updateVariantDisplay = (form) => {
    const card = form.closest(SELECTORS.card);
    const priceSource = getSelectedPriceSource(form);

    if (!card || !priceSource) return;

    const quantityInput = form.querySelector(SELECTORS.quantity);
    const quantity = Math.max(1, Number(quantityInput?.value || 1));

    const discountEnabled =
      priceSource.dataset.discountEnabled === 'true';

    const discountPercent = Number(
      priceSource.dataset.discountPercent || 0
    );

    const discountMinQuantity = Math.max(
      1,
      Number(priceSource.dataset.discountMinQuantity || 1)
    );

    const discountActive =
      discountEnabled &&
      discountPercent > 0 &&
      quantity >= discountMinQuantity;

    const isAvailable = isVariantAvailable(priceSource);

    const currentPrice = card.querySelector('.collection-current-price');
    const comparePrice = card.querySelector('.collection-compare-price');
    const saleText = card.querySelector('.collection-sale-text');
    const priceContainer = card.querySelector('.collection-card-price');
    const soldOutText = card.querySelector('.collection-sold-out-text');
    const submitButton = form.querySelector(SELECTORS.submit);
    const buttonText = form.querySelector(SELECTORS.buttonText);

    if (currentPrice) {
      currentPrice.textContent = discountActive
        ? priceSource.dataset.discountedPrice || ''
        : priceSource.dataset.basePrice || '';
    }

    if (comparePrice) {
      comparePrice.textContent = discountActive
        ? priceSource.dataset.basePrice || ''
        : '';

      comparePrice.hidden = !discountActive;
    }

    if (saleText) {
      saleText.textContent =
        priceContainer?.dataset.discountLabel ||
        `${discountPercent}% OFF`;

      saleText.hidden = !discountActive;
    }

    if (soldOutText) {
      soldOutText.hidden = isAvailable;
    }

    if (submitButton) {
      submitButton.disabled = !isAvailable;
    }

    if (buttonText) {
      buttonText.textContent = isAvailable
        ? 'ADD TO CART'
        : 'SOLD OUT';
    }

    setMessage(form);
  };

  const updateCartCount = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count < 1;
    });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const visibleCount =
        bubble.querySelector('span[aria-hidden="true"]') ||
        bubble.querySelector('span');

      if (visibleCount) {
        visibleCount.textContent = cart.item_count;
      }

      bubble.classList.toggle('hidden', cart.item_count < 1);
      bubble.hidden = cart.item_count < 1;
    });
  };

  const fetchCart = async () => {
    const response = await fetch(getCartUrl(), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error('The cart could not be refreshed.');
    }

    return response.json();
  };

  const dispatchCartEvents = (detail) => {
    const events = [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'ajaxProduct:added'
    ];

    events.forEach((eventName) => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail
        })
      );
    });

    window.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail
      })
    );
  };

  const openMiniCart = (cartComponent) => {
    if (cartComponent) {
      for (const methodName of ['open', 'show', 'reveal']) {
        if (typeof cartComponent[methodName] === 'function') {
          cartComponent[methodName]();
          return true;
        }
      }

      if (
        typeof HTMLDialogElement !== 'undefined' &&
        cartComponent instanceof HTMLDialogElement &&
        typeof cartComponent.showModal === 'function'
      ) {
        cartComponent.showModal();
        return true;
      }
    }

    const triggerSelectors = [
      '[data-cart-drawer-trigger]',
      '[data-cart-toggle="drawer"]',
      '[data-cart-toggle]',
      '.js-drawer-open-cart',
      '[aria-controls="CartDrawer"]',
      '[aria-controls="cart-drawer"]'
    ];

    for (const selector of triggerSelectors) {
      const trigger = document.querySelector(selector);
      if (trigger) {
        trigger.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        );
        return true;
      }
    }

    return false;
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest(SELECTORS.variant);

    if (select) {
      const form = select.closest(SELECTORS.form);
      if (form) updateVariantDisplay(form);
      return;
    }

    const quantityInput = event.target.closest(SELECTORS.quantity);

    if (quantityInput) {
      if (!quantityInput.value || Number(quantityInput.value) < 1) {
        quantityInput.value = '1';
      }

      const form = quantityInput.closest(SELECTORS.form);
      if (form) updateVariantDisplay(form);
    }
  });

  document.addEventListener('input', (event) => {
    const quantityInput = event.target.closest(SELECTORS.quantity);
    if (!quantityInput) return;

    const form = quantityInput.closest(SELECTORS.form);
    if (form) updateVariantDisplay(form);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest(SELECTORS.quantityButton);
    if (!button) return;

    const wrapper = button.closest('.collection-quantity-selector');
    const input = wrapper?.querySelector(SELECTORS.quantity);
    if (!input) return;

    const change = Number(button.dataset.quantityChange || 0);

    if (change > 0) {
      input.stepUp();
    } else if (change < 0) {
      input.stepDown();
    }

    if (!input.value || Number(input.value) < 1) {
      input.value = '1';
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target.closest(SELECTORS.form);
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton = form.querySelector(SELECTORS.submit);
      const buttonText = form.querySelector(SELECTORS.buttonText);

      if (!submitButton || submitButton.disabled) return;

      const originalText =
        buttonText?.textContent.trim() || 'ADD TO CART';

      submitButton.disabled = true;
      submitButton.classList.add('is-loading');

      if (buttonText) buttonText.textContent = 'ADDING…';
      setMessage(form);

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        const cartComponent = cartDrawer || cartNotification;
        const formData = new FormData(form);

        if (
          cartComponent &&
          typeof cartComponent.getSectionsToRender === 'function'
        ) {
          const sections = cartComponent
            .getSectionsToRender()
            .map((section) => section.id || section.section)
            .filter(Boolean);

          if (sections.length) {
            formData.set('sections', sections.join(','));
            formData.set('sections_url', window.location.pathname);
          }

          if (typeof cartComponent.setActiveElement === 'function') {
            cartComponent.setActiveElement(submitButton);
          }
        }

        const response = await fetch(getCartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'This product could not be added to the cart.'
          );
        }

        if (
          cartComponent &&
          result.sections &&
          typeof cartComponent.renderContents === 'function'
        ) {
          cartComponent.renderContents(result);
        }

        const cart = await fetchCart();
        updateCartCount(cart);

        dispatchCartEvents({
          cart,
          item: result,
          source: 'collection-product-card'
        });

        openMiniCart(cartComponent);

        if (buttonText) buttonText.textContent = 'ADDED';
        setMessage(form, 'Added to cart.', 'success');

        window.setTimeout(() => {
          if (buttonText) buttonText.textContent = originalText;
          setMessage(form);
        }, 1500);
      } catch (error) {
        console.error('Collection card add-to-cart error:', error);

        if (buttonText) buttonText.textContent = originalText;
        setMessage(
          form,
          error?.message || 'Something went wrong. Please try again.',
          'error'
        );
      } finally {
        const selectedIsAvailable = isVariantAvailable(
          getSelectedPriceSource(form)
        );

        submitButton.disabled = !selectedIsAvailable;
        submitButton.classList.remove('is-loading');
      }
    },
    true
  );
})();
